'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Hl, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/linear-algebra'

const VectorPlayground = dynamic(() => import('../_components/widgets/LinearWidgets').then(m => m.VectorPlayground), { ssr: false })
const Vector3D = dynamic(() => import('../_components/widgets/three/Vector3D'), { ssr: false })

export default function Vectors() {
  return (
    <Lesson>
      <Step>
        <H>A list of numbers is an arrow</H>
        <P>
          A <strong>vector</strong> is simply a list of numbers, like <M>{'[3, 2]'}</M>. But here’s the trick that makes it
          powerful: you can also see it as an <strong>arrow</strong> starting at 0 and ending at the point <M>{'(3, 2)'}</M> —
          three steps along <Hl c="blue">x</Hl>, two up <Hl c="amber">y</Hl>. Same thing, two pictures.
        </P>
        <Figure n="3.1" caption="Drag the tip of the arrow. Its two numbers (components) are how far it goes along each axis.">
          <VectorPlayground initialMode="components" />
        </Figure>
        <P>
          In machine learning, <em>everything</em> becomes a vector. A house is <M>{'[\\text{size}, \\text{rooms}, \\text{age}]'}</M>
          . A pixel is <M>{'[\\text{red}, \\text{green}, \\text{blue}]'}</M>. A word is a list of a few hundred numbers. Once
          something is a vector, all the math in this module works on it.
        </P>
        <Exercise id="feature-vectors" title="Things as vectors" {...EX.featureVectors} />
      </Step>

      <Step gate="q-add">
        <H>Adding and stretching arrows</H>
        <P>
          Adding two vectors adds their matching numbers: <M>{'[3, 2] + [-1, 2.5] = [2, 4.5]'}</M>. As arrows, that’s{' '}
          <strong>tip to tail</strong>: walk along the first arrow, then the second. Where you end up is the sum.
        </P>
        <P>
          Multiplying a vector by a single number (called a <strong>scalar</strong>) stretches it: <M>{'2 \\times [3, 2] = [6, 4]'}</M>
          — same direction, twice as long. A negative number flips it around.
        </P>
        <Figure n="3.2" caption="Switch between ‘add’ and ‘scale’. In add mode, drag both tips; the dashed arrows show the two ways to walk tip-to-tail.">
          <VectorPlayground initialMode="add" />
        </Figure>
        <Quiz
          id="q-add"
          question={<M>{'[1, -2] + 3 \\times [2, 1] = \\;?'}</M>}
          options={[{ label: <M>{'[7, 1]'}</M>, correct: true }, { label: <M>{'[9, -3]'}</M>, why: 'Scale first: 3 × [2, 1] = [6, 3]. Then add [1, −2].' }, { label: <M>{'[3, -1]'}</M>, why: 'That’s [1, −2] + [2, 1] — don’t forget to multiply by 3 first.' }]}
          explain="3 × [2, 1] = [6, 3], and [1, −2] + [6, 3] = [7, 1]. Multiplication before addition, just like with ordinary numbers."
        />
      </Step>

      <Step gate="nq-len">
        <H>How long is an arrow?</H>
        <P>
          The arrow <M>{'[3, 4]'}</M> forms a right-angled triangle with the axes. Pythagoras says the long side is{' '}
          <M>{'\\sqrt{3^2 + 4^2} = \\sqrt{25} = 5'}</M>. That’s the vector’s length, called its <strong>norm</strong>:
        </P>
        <MB>{'\\lVert \\mathbf{v} \\rVert = \\sqrt{v_1^2 + v_2^2 + \\dots + v_n^2}'}</MB>
        <Callout kind="notation">
          Bold letters like <M>{'\\mathbf{v}'}</M> are vectors; double bars <M>{'\\lVert \\mathbf{v} \\rVert'}</M> mean “length
          of”. <M>{'v_1, v_2, \\dots'}</M> are its entries. The <M>{'\\sqrt{\\;}'}</M> is a square root: the number that, squared,
          gives what’s inside.
        </Callout>
        <NumberQuiz id="nq-len" question={<>How long is the vector <M>{'[6, 8]'}</M>?</>} answer={10} explain={<>√(36 + 64) = √100 = 10. (It’s [3, 4] stretched by 2, so its length doubles too.)</>} />
      </Step>

      <Step>
        <H>More dimensions</H>
        <P>
          Add a third number and the arrow lifts off the page into 3D. The rules don’t change at all: add matching numbers,
          stretch by multiplying, length is the square root of the sum of squares.
        </P>
        <Figure n="3.3" caption="A vector in three dimensions. Drag to orbit; set the components with the sliders. The dashed box shows its x, y and z parts.">
          <Vector3D />
        </Figure>
        <Callout kind="plain">
          Nobody can picture 768 dimensions — not even AI researchers. But you don’t have to: the arithmetic is identical. Real
          models live in spaces with hundreds or thousands of dimensions, and everything you learn in 2D carries straight over.
        </Callout>
      </Step>

      <Step gate="vector-ops">
        <H>Build the toolkit</H>
        <Exercise
          id="vector-ops"
          title="add, scale, norm"
          {...EX.vectorOps}
          hints={[
            <>
              <C>{'a.map((ai, i) => ai + b[i])'}</C> adds matching entries.
            </>,
            <>
              For norm, add up <C>ai * ai</C> for every entry, then <C>Math.sqrt</C> the total.
            </>,
          ]}
        />
        <P>
          These now exist as built-ins too — type <C>vec.</C> in any editor to see <C>vec.add</C>, <C>vec.scale</C>,{' '}
          <C>vec.norm</C> and friends.
        </P>
      </Step>
    </Lesson>
  )
}
